import axios from 'axios';
import { Product, ProductUpdate } from '@domain/product/models/Product';
import { ProductFilter, ProductStatus } from '@domain/product/models/ProductFilter';
import { ProductFilterDTO } from '../dto/ProductFilterDTO';
import { ProductDataSource } from '../datasources/ProductDataSource';
import { AddProductPartDTO } from '../dto/AddProductPartDTO';
import { PaginatedResource, Resource } from '@domain/base/Resource';
import { IProductRepository } from '@domain/product/IProductRepository';
import { ProductMapper } from '../mappers/ProductMapper';
import { ShipmentMethod } from '@domain/shipment/models/ShipmentMethod';
import { ShipmentMethodMapper } from '@infrastructure/purchasing/mappers/ShipmentMethodMapper';
import { Marketplace } from '@domain/product/models/Marketplace';
import { ListingCountry } from '@domain/product/models/ListingCountry';


export class ProductRepository implements IProductRepository {

  constructor(
    private productDataSource: ProductDataSource
  ) { }

  async getProducts(filter?: ProductFilter, status: ProductStatus = ProductStatus.ACTIVE): Promise<PaginatedResource<Product[]>> {
    try {
      const unclassified = filter?.unclassified === true;
      const productsResponse = await this.productDataSource.getProducts({
        ...filter,
        active: unclassified ? undefined : status === ProductStatus.ACTIVE,
        unclassified: unclassified ? true : undefined,
        /** Default true when omitted — only pass false to include non-purchasable */
        purchasable: filter?.purchasable !== false,
      });

      return {
        status: 'success',
        ...productsResponse,
        data: productsResponse.data.map(ProductMapper.toModel),
      }
    } catch (error) {
      return { status: 'error', error: { type: 'UnknownError', message: 'Failed to get products' } };
    }
  }

  async getProduct(productId: string): Promise<Resource<Product>> {
    try {
      const product = await this.productDataSource.getProduct(productId);
      return { status: 'success', data: ProductMapper.toModel(product) };
    } catch (error) {
      return { status: 'error', error: { type: 'UnknownError', message: 'Failed to get product' } };
    }
  }

  async updateProduct(productId: string, updateData: ProductUpdate): Promise<Resource<Product>> {
    try {
      const updatedProduct = await this.productDataSource.updateProduct(productId, updateData);
      return { status: 'success', data: ProductMapper.toModel(updatedProduct) };
    } catch (error) {
      const message = axios.isAxiosError(error)
        ? (Array.isArray(error.response?.data?.message) ? error.response?.data?.message.join(', ') : error.response?.data?.message)
        : undefined;
      return { status: 'error', error: { type: 'UnknownError', message: message || 'Failed to update product' } };
    }
  }

  async bulkUpdateProducts(body: { updateData: Partial<Product>; filters?: ProductFilter }): Promise<Resource<void>> {
    try {
      const dto: { updateData: Partial<Product>; filters?: ProductFilterDTO } = {
        updateData: body.updateData,
      };
      if (body.filters) {
        dto.filters = this.toFilterDTO(body.filters);
      }
      await this.productDataSource.bulkUpdateProducts(dto);
      return { status: 'success', data: undefined };
    } catch (error) {
      return { status: 'error', error: { type: 'UnknownError', message: 'Failed to bulk update products' } };
    }
  }

  private toFilterDTO(filter: ProductFilter): ProductFilterDTO {
    return {
      modes: filter.modes,
      defaultShipmentMethod: filter.defaultShipmentMethod,
      page: filter.page,
      limit: filter.limit,
      productIds: filter.productIds,
      supplierIds: filter.supplierIds,
      collectionIds: filter.collectionIds,
      search: filter.search,
      forecastGrades: filter.forecastGrades,
      active: filter.active,
      purchasable: filter.purchasable !== false,
      marketplaceIds: filter.marketplaceIds,
      noMarketplaceListing: filter.noMarketplaceListing,
    };
  }

  async updateShipmentMethod(
    productId: string,
    shipmentMethodId: string
  ): Promise<Resource<ShipmentMethod>> {
    try {
      const shipmentMethod = await this.productDataSource.updatedefaultShipmentMethod(productId, shipmentMethodId)
      return {
        status: 'success',
        data: ShipmentMethodMapper.toModel(shipmentMethod)
      };
    } catch (error) {
      return {
        status: 'error',
        error: {
          type: 'UnknownError',
          message: 'Failed to update shipment method'
        }
      };
    }
  }

  async deleteProducts(filters: ProductFilter): Promise<Resource<void>> {
    try {
      await this.productDataSource.deleteProducts(this.toFilterDTO(filters));
      return { status: 'success', data: undefined };
    } catch (error) {
      return {
        status: 'error',
        error: {
          type: 'UnknownError',
          message: 'Failed to delete products'
        }
      };
    }
  }

  async getProductMarketplaces(): Promise<Resource<Marketplace[]>> {
    try {
      const data = await this.productDataSource.getProductMarketplaces();
      return {
        status: 'success', data
      };
    } catch (error) {
      return {
        status: 'error',
        error: {
          type: 'UnknownError',
          message: 'Failed to fetch product marketplaces'
        }
      };
    }
  }

  async getListingCountries(): Promise<Resource<ListingCountry[]>> {
    try {
      const data = await this.productDataSource.getListingCountries();
      return { status: 'success', data };
    } catch (error) {
      return {
        status: 'error',
        error: {
          type: 'UnknownError',
          message: 'Failed to fetch listing countries',
        },
      };
    }
  }

  async createProduct(data: Omit<Product, 'id'>): Promise<Resource<Product>> {
    try {

      const dto = ProductMapper.toCreateDTO(data);
      const product = await this.productDataSource.createProduct(dto);
      return { status: 'success', data: ProductMapper.toModel(product) };
    } catch (error) {
      return {
        status: 'error',
        error: {
          type: 'UnknownError',
          message: 'Failed to create product'
        }
      };
    }
  }

  async addProductParts(productId: string, data: AddProductPartDTO[]): Promise<Resource<Product>> {
    try {
      const product = await this.productDataSource.addProductParts(productId, data);
      return { status: 'success', data: ProductMapper.toModel(product) };
    } catch {
      return {
        status: 'error',
        error: {
          type: 'UnknownError',
          message: 'Failed to add product part'
        }
      };
    }
  }

  async removeProductPart(productId: string, componentId: string): Promise<Resource<void>> {
    try {
      await this.productDataSource.removeProductPart(productId, componentId);
      return { status: 'success', data: undefined };
    } catch {
      return {
        status: 'error',
        error: {
          type: 'UnknownError',
          message: 'Failed to remove product part'
        }
      };
    }
  }

  async addProductUsedIn(productId: string, data: AddProductPartDTO[]): Promise<Resource<Product>> {
    try {
      const product = await this.productDataSource.addProductUsedIn(productId, data);
      return { status: 'success', data: ProductMapper.toModel(product) };
    } catch {
      return { status: 'error', error: { type: 'UnknownError', message: 'Failed to add product used in' } };
    }
  }

  async removeProductUsedIn(productId: string, componentId: string): Promise<Resource<void>> {
    try {
      await this.productDataSource.removeProductUsedIn(productId, componentId);
      return { status: 'success', data: undefined };
    } catch {
      return { status: 'error', error: { type: 'UnknownError', message: 'Failed to remove product used in' } };
    }
  }
}
